import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { interval } from 'rxjs';
import { environment } from 'src/environments/environment';

// Capturar el evento globalmente ANTES de que Angular se inicialice
let globalDeferredPrompt: any = null;
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  globalDeferredPrompt = event;
});

@Injectable({ providedIn: 'root' })
export class PWAStore {
  deferredPrompt = signal<any>(null);
  showInstallButton = signal(false);
  showUpdateButton = signal(false);
  forceUpdate = true;

  // Evita que los distintos disparadores de actualización (version polling, SW
  // VERSION_READY, recovery) compitan y disparen múltiples recargas en paralelo.
  private updating = false;

  constructor(
    private swUpdate: SwUpdate,
    private matSnackBar: MatSnackBar,
    private router: Router
  ) {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    // Siempre configurar la verificación de actualizaciones (incluso en standalone)
    this.setupUpdateChecking();

    // Verificación de versión independiente del Service Worker
    this.setupVersionPolling();

    // Si ya está instalada como standalone, no mostrar el botón de instalar
    if (isStandalone) {
      return;
    }
    
    // Si el evento ya se capturó antes de que Angular se inicializara, usarlo
    if (globalDeferredPrompt) {
      this.deferredPrompt.set(globalDeferredPrompt);
      this.showInstallButton.set(true);
      globalDeferredPrompt = null;
    }
    
    // Seguir escuchando por si el evento se dispara después
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt.set(event)
      this.showInstallButton.set(true);
    });
  }

  private setupUpdateChecking() {
    if (!this.swUpdate.isEnabled) {
      console.warn('[PWA] Service Worker is not enabled — SW update checking disabled. Version polling will handle updates.');
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        console.log('[PWA] New version ready, current hash:', (event as any).currentVersion?.hash, 'latest hash:', (event as any).latestVersion?.hash);
        this.showUpdateButton.set(true);
        if (this.forceUpdate) {
          this.updatePWA();
        }
      }

      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.error('[PWA] Version installation failed:', (event as any).error);
        this.recoverFromBrokenState();
      }
    });

    // Manejar estado irrecuperable del Service Worker
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('[PWA] Unrecoverable state:', event.reason);
      this.recoverFromBrokenState();
    });

    // Verificar actualizaciones cada 30 segundos
    interval(30 * 1000).subscribe(() => {
      this.swUpdate.checkForUpdate().then(hasUpdate => {
        if (hasUpdate) {
          console.log('[PWA] Update found during periodic check');
        }
      });
    });

    // Verificar inmediatamente al iniciar
    this.swUpdate.checkForUpdate().then(hasUpdate => {
      if (hasUpdate) {
        console.log('[PWA] Update available on startup');
      }
    });
  }

  /**
   * Verificación de versión independiente del Service Worker.
   * Hace fetch de /version.json con cache-bust cada 60 segundos.
   * Si la versión del servidor difiere de la local, fuerza reload.
   */
  private setupVersionPolling() {
    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.version && data.version !== environment.appVersion) {
          console.log(`[PWA] Version mismatch detected: local=${environment.appVersion}, server=${data.version}. Updating...`);
          // IMPORTANTE: un window.location.reload() "pelado" NO bypasea el Service
          // Worker: al recargar, el SW vuelve a servir el bundle viejo cacheado, así
          // que environment.appVersion sigue siendo el viejo y el mismatch persiste
          // -> bucle de recargas. Hay que actualizar el bundle cacheado del SW (o
          // desregistrarlo) ANTES de recargar para que la nueva versión se cargue.
          await this.applyUpdateAndReload();
        }
      } catch {
        // Silenciar errores de red — el polling reintentará
      }
    };

    // Verificar al iniciar (con delay para no bloquear el bootstrap)
    setTimeout(checkVersion, 10_000);

    // Verificar cada 60 segundos
    interval(60_000).subscribe(() => checkVersion());
  }

  /**
   * Aplica la actualización de forma consciente del Service Worker y recarga.
   * Garantiza que la nueva versión del bundle se cargue (no un reload "pelado"
   * que volvería a servir el bundle viejo cacheado por el SW -> bucle).
   */
  private async applyUpdateAndReload() {
    if (this.updating) return;
    this.updating = true;

    try {
      if (this.swUpdate.isEnabled) {
        // Asegurar que el SW tenga la última versión descargada y activarla,
        // de modo que el reload sirva el bundle nuevo desde la cache del SW.
        const hasUpdate = await this.swUpdate.checkForUpdate();
        const versionReady = this.showUpdateButton();
        if (hasUpdate || versionReady) {
          await this.swUpdate.activateUpdate();
          window.location.reload();
          return;
        }
        // Si el SW no detecta una versión nueva pero version.json SÍ difiere, su
        // cache está rancia y un activateUpdate sería no-op -> recaería en bucle.
        // Caemos a la recuperación dura (limpiar caches + desregistrar) para
        // garantizar que la próxima carga baje el bundle fresco desde la red.
      }
    } catch (err) {
      console.error('[PWA] activateUpdate failed, falling back to cache recovery:', err);
    }

    // SW deshabilitado o falló la activación: limpiar caches + desregistrar SW
    // antes de recargar para forzar una descarga fresca desde la red.
    await this.recoverFromBrokenState();
  }

  private async recoverFromBrokenState() {
    console.warn('[PWA] Recovering from broken state: clearing caches and reloading...');
    // Limpiar todas las caches del Service Worker
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
    // Desregistrar el Service Worker corrupto
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }
    window.location.reload();
  }

  installPWA() {
    window.addEventListener('appinstalled', () => {
      this.matSnackBar.open('Se instaló la app', 'Ok', {
        duration: 4000,
        panelClass: 'mat-snack-success'
			});
			this.showInstallButton.set(false);

    });

    const prompt = this.deferredPrompt();
    if (prompt) {
      prompt.prompt();

      prompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        this.deferredPrompt.set(null);
      });
    }
  }

  async updatePWA() {
    if (this.updating) return;
    this.updating = true;
    try {
      // Activar la nueva versión del service worker ANTES de recargar
      await this.swUpdate.activateUpdate();
      console.log('[PWA] Update activated, reloading...');
      this.matSnackBar.open('Nueva versión instalada, recargando...', 'Ok', {
        duration: 3000,
        panelClass: 'mat-snack-info'
      });
      // Dar tiempo al snackbar antes de recargar
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      console.error('[PWA] Failed to activate update, forcing recovery:', err);
      await this.recoverFromBrokenState();
    }
  }

  setForceUpdate(value: boolean) {
    this.forceUpdate = value;
    if (this.forceUpdate && this.showUpdateButton()) {
      this.updatePWA();
    }
  }
}
